import { useEffect, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface WidgetCache<T> {
  data: T | null;
  lastFetched: number | null; // Timestamp
}

interface ExploreWidgetsState {
  birthdays: WidgetCache<any>;
  anniversaries: WidgetCache<any>;
  endorsements: WidgetCache<any>;
  coinsRanking: WidgetCache<any>;
  corporateRanking: WidgetCache<any>;
  externalLinks: WidgetCache<any>;
  benefits: WidgetCache<any>;
  banners: WidgetCache<any>;
  events: WidgetCache<any>;
  hasHydrated: boolean;

  setBirthdays: (data: any) => void;
  setAnniversaries: (data: any) => void;
  setEndorsements: (data: any) => void;
  setCoinsRanking: (data: any) => void;
  setCorporateRanking: (data: any) => void;
  setExternalLinks: (data: any) => void;
  setBenefits: (data: any) => void;
  setBanners: (data: any) => void;
  invalidateBanners: () => void;
  setEvents: (data: any) => void;
  setHasHydrated: (hasHydrated: boolean) => void;
}

export const useExploreWidgetsStore = create<ExploreWidgetsState>()(
  persist(
    (set) => ({
      birthdays: { data: null, lastFetched: null },
      anniversaries: { data: null, lastFetched: null },
      endorsements: { data: null, lastFetched: null },
      coinsRanking: { data: null, lastFetched: null },
      corporateRanking: { data: null, lastFetched: null },
      externalLinks: { data: null, lastFetched: null },
      benefits: { data: null, lastFetched: null },
      banners: { data: null, lastFetched: null },
      events: { data: null, lastFetched: null },
      hasHydrated: false,

      setBirthdays: (data) =>
        set({ birthdays: { data, lastFetched: Date.now() } }),
      setAnniversaries: (data) =>
        set({ anniversaries: { data, lastFetched: Date.now() } }),
      setEndorsements: (data) =>
        set({ endorsements: { data, lastFetched: Date.now() } }),
      setCoinsRanking: (data) =>
        set({ coinsRanking: { data, lastFetched: Date.now() } }),
      setCorporateRanking: (data) =>
        set({ corporateRanking: { data, lastFetched: Date.now() } }),
      setExternalLinks: (data) =>
        set({ externalLinks: { data, lastFetched: Date.now() } }),
      setBenefits: (data) =>
        set({ benefits: { data, lastFetched: Date.now() } }),
      setBanners: (data) => set({ banners: { data, lastFetched: Date.now() } }),
      invalidateBanners: () =>
        set({ banners: { data: null, lastFetched: null } }),
      setEvents: (data) => set({ events: { data, lastFetched: Date.now() } }),
      setHasHydrated: (hasHydrated) => set({ hasHydrated })
    }),
    {
      name: "explore-widgets-cache-v2",
      partialize: (state) => ({
        birthdays: state.birthdays,
        anniversaries: state.anniversaries,
        endorsements: state.endorsements,
        coinsRanking: state.coinsRanking,
        corporateRanking: state.corporateRanking,
        externalLinks: state.externalLinks,
        benefits: state.benefits,
        banners: state.banners,
        events: state.events
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      }
    }
  )
);

const DEFAULT_CACHE_REFRESH_HOUR = 3; // 03:00 AM hora local del navegador

const getLastScheduledRefreshTime = (
  refreshHour = DEFAULT_CACHE_REFRESH_HOUR
) => {
  const now = new Date();
  const refreshTime = new Date();

  refreshTime.setHours(refreshHour, 0, 0, 0);

  if (now.getTime() < refreshTime.getTime()) {
    refreshTime.setDate(refreshTime.getDate() - 1);
  }

  return refreshTime.getTime();
};

const getNextScheduledRefreshTime = (
  refreshHour = DEFAULT_CACHE_REFRESH_HOUR
) => {
  const now = new Date();
  const refreshTime = new Date();

  refreshTime.setHours(refreshHour, 0, 0, 0);

  if (now.getTime() >= refreshTime.getTime()) {
    refreshTime.setDate(refreshTime.getDate() + 1);
  }

  return refreshTime.getTime();
};

export const useExploreWidgetsCacheRefresh = (
  refreshHour = DEFAULT_CACHE_REFRESH_HOUR
) => {
  const [, setRefreshSignal] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let timeoutId: number;

    const scheduleNextRefresh = () => {
      const delay = Math.max(
        getNextScheduledRefreshTime(refreshHour) - Date.now(),
        1000
      );

      timeoutId = window.setTimeout(() => {
        setRefreshSignal(Date.now());
        scheduleNextRefresh();
      }, delay);
    };

    scheduleNextRefresh();

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [refreshHour]);
};

export const isCacheValid = (
  lastFetched: number | null,
  refreshHour = DEFAULT_CACHE_REFRESH_HOUR
) => {
  if (!lastFetched) return false;

  return lastFetched >= getLastScheduledRefreshTime(refreshHour);
};
